import { Badge } from '@folio/components/ui/badge';
import { Button } from '@folio/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@folio/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@folio/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components/ui/table';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  branchDtoBranchStatusEnum,
  documentsControllerGetDocumentsQueryOptions,
  signatureControllerGetSignatureRequestsByExternalIdQueryOptions,
  signatureControllerGetSignerLink,
  signatureControllerResendSignatureReminder,
  keyValueFieldsToObject,
  SignatureRequest,
} from 'korint-sdk';
import { get } from 'lodash';
import { ChevronDown, ChevronRight, Copy, Eye, Mail, MoreVertical, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components';
import { DocumentViewer } from '../../components/features/document-viewer/DocumentViewer';
import type { DocumentViewerDocument } from '../../components/features/document-viewer/types';
import { formatDateWithTime } from '../../lib/date-formatting';
import { useSignatureStatuses } from '../../hooks/useSignatureStatuses';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import { StatusPill } from '../../components/ui/status-pill';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

function SignerActions({
  signature,
  signer,
  policyId,
  isBranchClosed,
}: {
  signature: SignatureRequest;
  signer: SignatureRequest['signers'][number];
  policyId: string;
  isBranchClosed: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  if (isBranchClosed) return null;
  if (signature.status !== 'PENDING' || (signer.status && signer.status !== 'PENDING')) return null;

  const handleResend = async () => {
    setLoading(true);
    try {
      const res = await signatureControllerResendSignatureReminder(
        policyId,
        signature.id,
        signer.customerId,
      );
      toast({
        title: t('signatureTracking.actions.resendSuccess'),
        description: t('signatureTracking.actions.resendSuccessDescription', {
          count: res.remindedSigners,
        }),
      });
    } catch (error) {
      const errorCode = get(error, 'errorResponse.data.code') as string | undefined;
      const errorKey =
        errorCode && t(`signatureTracking.actions.errors.${errorCode}`, '') !== ''
          ? `signatureTracking.actions.errors.${errorCode}`
          : 'signatureTracking.actions.resendErrorDescription';
      toast({
        title: t('signatureTracking.actions.resendError'),
        description: t(errorKey),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const res = await signatureControllerGetSignerLink(policyId, signature.id, signer.customerId);
      await navigator.clipboard.writeText(res.url);
      toast({
        title: t('signatureTracking.actions.copySuccess'),
        description: t('signatureTracking.actions.copySuccessDescription'),
      });
    } catch (error) {
      const errorCode = get(error, 'errorResponse.data.code') as string | undefined;
      const errorKey =
        errorCode && t(`signatureTracking.actions.errors.${errorCode}`, '') !== ''
          ? `signatureTracking.actions.errors.${errorCode}`
          : 'signatureTracking.actions.copyErrorDescription';
      toast({
        title: t('signatureTracking.actions.copyError'),
        description: t(errorKey),
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleResend} disabled={loading}>
          <Mail className="mr-2 h-4 w-4" />
          {t('signatureTracking.actions.resend')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyLink}>
          <Copy className="mr-2 h-4 w-4" />
          {t('signatureTracking.actions.copyLink')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SignatureDocuments({ signature }: { signature: SignatureRequest }) {
  const { policy } = usePolicyOnDashboard();
  const { t } = useTranslation();
  const [selectedDocument, setSelectedDocument] = useState<DocumentViewerDocument | null>(null);
  const documentIds = signature.documents.map(d => d.id);
  const { data, isLoading } = useQuery({
    ...documentsControllerGetDocumentsQueryOptions({ ids: documentIds }),
    staleTime: 30_000,
  });

  const documents = data?.documents ?? [];

  const viewerDocuments: DocumentViewerDocument[] = useMemo(
    () =>
      documents.map(doc => ({
        id: doc.id,
        name: doc.name,
        signedUrl: doc.signedUrl,
        createdAt: doc.createdAt,
        type: doc.type,
        category: doc.category,
        metadata: keyValueFieldsToObject(doc.metadata ?? []),
      })),
    [documents],
  );

  if (isLoading) {
    return (
      <Text tone="muted" size="sm">
        {t('signatureTracking.documents.loading')}
      </Text>
    );
  }

  if (documents.length === 0) {
    return (
      <Text tone="muted" size="sm">
        {t('signatureTracking.documents.empty')}
      </Text>
    );
  }

  return (
    <>
      <Stack gap={2}>
        {viewerDocuments.map(doc => {
          const docType = (doc.category as string) || 'unknown';
          return (
            <Group key={doc.id} justify="between" gap={3} className="rounded-md border px-3 py-2">
              <Group gap={2} className="min-w-0">
                <Text inline truncate size="sm">
                  {doc.name}
                </Text>
                <Badge variant="outline">{t(`documents.${docType}`, docType)}</Badge>
              </Group>
              <button
                type="button"
                onClick={() => setSelectedDocument(doc)}
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <Eye className="h-4 w-4" />
              </button>
            </Group>
          );
        })}
      </Stack>
      <DocumentViewer
        productId={policy.productId}
        document={selectedDocument}
        documents={viewerDocuments}
        onClose={() => setSelectedDocument(null)}
        onDocumentChange={setSelectedDocument}
      />
    </>
  );
}

function SingleSignerRows({
  signature,
  policyId,
  isBranchClosed,
}: {
  signature: SignatureRequest;
  policyId: string;
  isBranchClosed: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const signatureStatuses = useSignatureStatuses();
  const isExpandable = signature.documents.length > 0;
  const signer = signature.signers[0];

  return (
    <>
      <TableRow
        className={isExpandable ? 'cursor-pointer' : ''}
        onClick={() => isExpandable && setIsExpanded(o => !o)}
      >
        <TableCell className="w-8">
          {isExpandable ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </TableCell>
        <TableCell className="font-medium">
          {signer.firstName} {signer.lastName}
        </TableCell>
        <TableCell className="text-sm">{signer.email}</TableCell>
        <TableCell className="text-sm">{signature.documents.length}</TableCell>
        <TableCell className="whitespace-nowrap">
          <StatusPill
            color={signatureStatuses[signature.status]}
            label={t(`signatureTracking.status.${signature.status}`)}
          />
        </TableCell>
        <TableCell className="text-sm">
          {signature.createdAt ? formatDateWithTime(new Date(signature.createdAt)) : '-'}
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <SignerActions
            signature={signature}
            signer={signer}
            policyId={policyId}
            isBranchClosed={isBranchClosed}
          />
        </TableCell>
      </TableRow>

      {isExpandable && isExpanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/50 p-4">
            <SignatureDocuments signature={signature} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function MultiSignerRows({
  signature,
  policyId,
  isBranchClosed,
}: {
  signature: SignatureRequest;
  policyId: string;
  isBranchClosed: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const signatureStatuses = useSignatureStatuses();

  return (
    <>
      <TableRow className="cursor-pointer" onClick={() => setIsExpanded(o => !o)}>
        <TableCell className="w-8">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-medium whitespace-nowrap">
          <Group gap={2}>
            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>
              {t('signatureTracking.multiSigner.label', {
                count: signature.signers.length,
              })}
            </span>
          </Group>
        </TableCell>
        <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
          {signature.signers.map(s => s.email).join(', ')}
        </TableCell>
        <TableCell className="text-sm">{signature.documents.length}</TableCell>
        <TableCell className="whitespace-nowrap">
          <StatusPill
            color={signatureStatuses[signature.status]}
            label={t(`signatureTracking.status.${signature.status}`)}
          />
        </TableCell>
        <TableCell className="text-sm">
          {signature.createdAt ? formatDateWithTime(new Date(signature.createdAt)) : '-'}
        </TableCell>
        <TableCell />
      </TableRow>

      {isExpanded && (
        <>
          {signature.signers.map(signer => {
            const signerStatus = signer.status ?? 'PENDING';
            return (
              <TableRow key={`${signature.id}-${signer.customerId}`} className="bg-muted/30">
                <TableCell className="w-8 py-1.5" />
                <TableCell className="pl-10 font-medium text-sm py-1.5">
                  {signer.firstName} {signer.lastName}
                </TableCell>
                <TableCell className="text-sm py-1.5">{signer.email}</TableCell>
                <TableCell className="py-1.5" />
                <TableCell className="whitespace-nowrap py-1.5">
                  <StatusPill
                    color={signatureStatuses[signerStatus]}
                    label={t(`signatureTracking.signerStatus.${signerStatus}`)}
                  />
                </TableCell>
                <TableCell className="py-1.5" />
                <TableCell className="py-1.5" onClick={e => e.stopPropagation()}>
                  <SignerActions
                    signature={signature}
                    signer={signer}
                    policyId={policyId}
                    isBranchClosed={isBranchClosed}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {signature.documents.length > 0 && (
            <TableRow>
              <TableCell colSpan={7} className="bg-muted/50 p-4">
                <SignatureDocuments signature={signature} />
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </>
  );
}

function EmptySignerRow({ signature }: { signature: SignatureRequest }) {
  const { t } = useTranslation();
  const signatureStatuses = useSignatureStatuses();

  return (
    <TableRow>
      <TableCell className="w-8" />
      <TableCell className="text-muted-foreground text-sm italic">
        {t('signatureTracking.noSigners')}
      </TableCell>
      <TableCell />
      <TableCell className="text-sm">{signature.documents.length}</TableCell>
      <TableCell className="whitespace-nowrap">
        <StatusPill
          color={signatureStatuses[signature.status]}
          label={t(`signatureTracking.status.${signature.status}`)}
        />
      </TableCell>
      <TableCell className="text-sm">
        {signature.createdAt ? formatDateWithTime(new Date(signature.createdAt)) : '-'}
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

function SignatureRequestRows({
  signature,
  policyId,
  isBranchClosed,
}: {
  signature: SignatureRequest;
  policyId: string;
  isBranchClosed: boolean;
}) {
  if (signature.signers.length === 0) {
    return <EmptySignerRow signature={signature} />;
  }
  if (signature.signers.length > 1) {
    return (
      <MultiSignerRows signature={signature} policyId={policyId} isBranchClosed={isBranchClosed} />
    );
  }
  return (
    <SingleSignerRows signature={signature} policyId={policyId} isBranchClosed={isBranchClosed} />
  );
}

export function SignatureTrackingModule() {
  const { policy, branches } = usePolicyOnDashboard();
  const { t } = useTranslation();

  const { data } = useSuspenseQuery(
    signatureControllerGetSignatureRequestsByExternalIdQueryOptions(policy.id),
  );

  const signatureRequests = data.signatureRequests ?? [];

  const branchesById = useMemo(
    () => new Map(branches?.data?.map(b => [b.id, b]) ?? []),
    [branches?.data],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('signatureTracking.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {signatureRequests.length === 0 ? (
          <Text tone="muted" size="sm">
            {t('signatureTracking.noSignatures')}
          </Text>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t('signatureTracking.columns.name')}</TableHead>
                <TableHead>{t('signatureTracking.columns.email')}</TableHead>
                <TableHead>{t('signatureTracking.columns.documents')}</TableHead>
                <TableHead>{t('signatureTracking.columns.status')}</TableHead>
                <TableHead>{t('signatureTracking.columns.sentAt')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {signatureRequests.map(signature => {
                const sigBranchId = signature.metadata?.find(m => m.key === 'branchId')?.value;
                const branch = sigBranchId ? branchesById.get(sigBranchId) : undefined;
                const isBranchClosed =
                  branch?.branchStatus === branchDtoBranchStatusEnum.BRANCH_CLOSED;
                return (
                  <SignatureRequestRows
                    key={signature.id}
                    signature={signature}
                    policyId={policy.id}
                    isBranchClosed={isBranchClosed}
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
